import initSqlJs, { type Database, type SqlJsStatic, type SqlValue } from 'sql.js';
import sqlWasmUrl from 'sql.js/dist/sql-wasm.wasm?url';
import { CONTENT_PACK_SCHEMA_SQL, CONTENT_PACK_TABLES, type CompiledPack } from './contentPackSchema';
import { stableStringify } from './hash';

let sqlPromise: Promise<SqlJsStatic> | null = null;
export function loadSqlJs(): Promise<SqlJsStatic> {
  const testWasmPath = new URL('../../node_modules/sql.js/dist/sql-wasm.wasm', import.meta.url).pathname.replace(/^\/([A-Za-z]:)/, '$1');
  sqlPromise ??= initSqlJs({ locateFile: () => import.meta.env.MODE === 'test' ? testWasmPath : sqlWasmUrl });
  return sqlPromise;
}

function insertRecords(db: Database, table: string, records: Array<Record<string, unknown>>): void {
  if (records.length === 0) return;
  const columns = Object.keys(records[0]);
  const statement = db.prepare(`INSERT INTO ${table} (${columns.join(',')}) VALUES (${columns.map(() => '?').join(',')})`);
  try { for (const record of records) statement.run(columns.map((column) => asSqlValue(record[column]))); }
  finally { statement.free(); }
}

function recordPuzzle(record: Record<string, unknown>): string {
  return String(record.puzzle_id ?? record.id ?? '<unknown>');
}

export function assertUniqueCompiledIds(
  table: string,
  records: Array<Record<string, unknown>>,
  idColumn = 'id',
): void {
  const seen = new Map<string, Record<string, unknown>>();
  for (const record of records) {
    const id = String(record[idColumn]);
    const first = seen.get(id);
    if (first) {
      throw new Error([
        `Duplicate compiled ID in ${table}:`,
        `id=${id}`,
        `first puzzle=${recordPuzzle(first)}`,
        `second puzzle=${recordPuzzle(record)}`,
      ].join('\n'));
    }
    seen.set(id, record);
  }
}

function asSqlValue(value: unknown): SqlValue {
  if (value === null || value === undefined || typeof value === 'string' || typeof value === 'number' || value instanceof Uint8Array) return value ?? null;
  throw new Error(`Valeur SQLite non scalaire : ${String(value)}`);
}

export function validateContentDatabase(db: Database): void {
  const integrity = db.exec('PRAGMA integrity_check');
  if (integrity[0]?.values[0]?.[0] !== 'ok') throw new Error(`SQLite integrity_check a échoué : ${String(integrity[0]?.values[0]?.[0])}`);
  const tables = new Set((db.exec("SELECT name FROM sqlite_master WHERE type='table'")[0]?.values ?? []).map((row) => String(row[0])));
  for (const table of CONTENT_PACK_TABLES) if (!tables.has(table)) throw new Error(`Table SQLite obligatoire absente : ${table}`);
  const expected: Record<string, string[]> = {
    metadata: ['key', 'value'], content_packs: ['id', 'language_code', 'version', 'title', 'description', 'status', 'imported_at'],
    categories: ['id', 'language_code', 'title', 'description', 'icon_key', 'sort_order', 'is_active'],
    puzzles: ['id', 'content_pack_id', 'category_id', 'language_code', 'title_internal', 'answer_display', 'answer_normalized', 'source_text', 'difficulty_estimated', 'is_daily_eligible', 'status', 'sort_order', 'revision', 'created_at', 'updated_at'],
    puzzle_aliases: ['id', 'puzzle_id', 'display', 'normalized', 'type', 'is_full_answer'],
    puzzle_answer_words: ['id', 'puzzle_id', 'word_index', 'word_display', 'word_normalized', 'equivalence_group_key', 'can_be_revealed_by_player', 'can_be_revealed_by_hint'],
    puzzle_tokens: ['id', 'puzzle_id', 'token_index', 'token_type', 'original_text', 'normalized_text', 'equivalence_group_key', 'word_group_key', 'char_length', 'is_revealable', 'is_hint_eligible', 'is_answer_word', 'is_manually_excluded', 'answer_word_index'],
    puzzle_word_groups: ['id', 'puzzle_id', 'group_key', 'display_label', 'normalized_variants_json', 'original_variants_json', 'first_token_index', 'occurrence_count', 'is_revealable', 'is_hint_eligible', 'is_answer_group', 'is_manually_excluded'],
    equivalence_rules: ['id', 'content_pack_id', 'puzzle_id', 'language_code', 'scope', 'group_key', 'variants_json', 'rule_type', 'sort_order'],
  };
  for (const [table, columns] of Object.entries(expected)) {
    const actual = new Set((db.exec(`PRAGMA table_info(${table})`)[0]?.values ?? []).map((row) => String(row[1])));
    for (const column of columns) if (!actual.has(column)) throw new Error(`Colonne SQLite obligatoire absente : ${table}.${column}`);
  }
}

export async function writePackSqlite(compiled: CompiledPack, releaseId: number): Promise<Uint8Array> {
  const SQL = await loadSqlJs();
  const db = new SQL.Database();
  try {
    db.run('PRAGMA journal_mode=OFF; PRAGMA synchronous=OFF;');
    db.run(CONTENT_PACK_SCHEMA_SQL);
    const metadata = [
      ['content_schema_version', '2'], ['kind', 'pack'], ['release_id', String(releaseId)],
      ['generated_at', compiled.generatedAt], ['content_pack_id', compiled.pack.id],
      ['language_code', compiled.pack.language_code], ['pack_version', String(compiled.version)],
      ['puzzle_count', String(compiled.puzzles.length)],
    ].map(([key, value]) => ({ key, value }));
    const contentPacks = [{ id: compiled.pack.id, language_code: compiled.pack.language_code, version: compiled.version, title: compiled.pack.name, description: compiled.pack.description, status: 'active', imported_at: compiled.generatedAt }];
    const categories = [...compiled.categories].sort((a, b) => a.sort_order - b.sort_order || a.id.localeCompare(b.id)).map((category) => ({ id: category.id, language_code: compiled.pack.language_code, title: category.label, description: category.description, icon_key: category.icon_key, sort_order: category.sort_order, is_active: category.is_active ? 1 : 0 }));
    const puzzles = [...compiled.puzzles].sort((a, b) => String(a.puzzle.id).localeCompare(String(b.puzzle.id)));
    const puzzleRows = puzzles.map((item) => item.puzzle);
    const aliases = puzzles.flatMap((item) => item.aliases);
    const answerWords = puzzles.flatMap((item) => item.answerWords);
    const tokens = puzzles.flatMap((item) => item.tokens);
    const wordGroups = puzzles.flatMap((item) => item.wordGroups);
    const globalRuleRows = compiled.globalRules.map((rule) => ({ id: rule.id, content_pack_id: rule.content_pack_id, puzzle_id: rule.puzzle_id, language_code: rule.language_code, scope: rule.scope, group_key: rule.group_key, variants_json: JSON.stringify([...rule.variants].sort()), rule_type: rule.rule_type, sort_order: rule.sort_order }));
    const globalRuleSignatures = new Map(globalRuleRows.map((rule) => [String(rule.id), stableStringify(rule)]));
    const puzzleRuleRows = puzzles.flatMap((puzzle) => puzzle.equivalenceRules)
      .filter((rule) => globalRuleSignatures.get(String(rule.id)) !== stableStringify(rule));
    const rules = [...puzzleRuleRows, ...globalRuleRows]
      .sort((a, b) => String(a.id).localeCompare(String(b.id)));

    const tables: Array<[string, Array<Record<string, unknown>>, string?]> = [
      ['metadata', metadata, 'key'],
      ['content_packs', contentPacks],
      ['categories', categories],
      ['puzzles', puzzleRows],
      ['puzzle_aliases', aliases],
      ['puzzle_answer_words', answerWords],
      ['puzzle_tokens', tokens],
      ['puzzle_word_groups', wordGroups],
      ['equivalence_rules', rules],
    ];
    for (const [table, records, idColumn] of tables) assertUniqueCompiledIds(table, records, idColumn);

    db.run('BEGIN');
    for (const [table, records] of tables) insertRecords(db, table, records);
    db.run('COMMIT');
    validateContentDatabase(db);
    return db.export();
  } catch (error) { try { db.run('ROLLBACK'); } catch { /* no transaction */ } throw error; }
  finally { db.close(); }
}

export async function inspectSqlite(bytes: Uint8Array): Promise<Database> {
  const SQL = await loadSqlJs();
  const db = new SQL.Database(bytes);
  validateContentDatabase(db);
  return db;
}
